import React, { useEffect, useRef, useState, useCallback } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { QueryBuilderExportRecord } from './types';
import { listQueryBuilderExports } from '../../../api/admin';

// ---- Helpers ----

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const relative = formatDistanceToNow(date, { addSuffix: true });
  const absolute = format(date, "MMM d, yyyy 'at' h:mmaaa");
  return `${relative} — ${absolute}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function truncateList(items: string[], maxVisible: number): { visible: string[]; overflow: string[] } {
  if (items.length <= maxVisible) {
    return { visible: items, overflow: [] };
  }
  return { visible: items.slice(0, maxVisible), overflow: items.slice(maxVisible) };
}

function hasActiveJobs(exports: QueryBuilderExportRecord[]): boolean {
  return exports.some((e) => e.status === 'QUEUED' || e.status === 'RUNNING');
}

// ---- Sub-components ----

const StatusPill: React.FC<{ record: QueryBuilderExportRecord }> = ({ record }) => {
  const { status, errorMessage } = record;

  if (status === 'QUEUED') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        Queued
      </span>
    );
  }

  if (status === 'RUNNING') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
        <svg
          className="animate-spin h-3 w-3 text-indigo-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        Running…
      </span>
    );
  }

  if (status === 'SUCCEEDED') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Ready
      </span>
    );
  }

  // FAILED
  const title = errorMessage ? `Error: ${errorMessage}` : 'Export failed';
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 cursor-help"
      title={title}
    >
      Failed
    </span>
  );
};

interface TagListProps {
  items: string[];
  maxVisible?: number;
}

const TagList: React.FC<TagListProps> = ({ items, maxVisible = 4 }) => {
  const { visible, overflow } = truncateList(items, maxVisible);
  const overflowTitle = overflow.join(', ');

  return (
    <span>
      {visible.join(', ')}
      {overflow.length > 0 && (
        <span
          className="ml-1 text-gray-400 cursor-help"
          title={overflowTitle}
        >
          +{overflow.length} more
        </span>
      )}
    </span>
  );
};

interface ExportRowProps {
  record: QueryBuilderExportRecord;
}

const ExportRow: React.FC<ExportRowProps> = ({ record }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 px-4 border-b border-gray-100 last:border-b-0">
      {/* Left column: timestamp + dimensions/metrics */}
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-xs text-gray-500">{formatTimestamp(record.enqueuedAt)}</p>
        <p className="text-sm text-gray-700">
          <span className="font-medium text-gray-600">Metrics:</span>{' '}
          <span className="text-gray-500">
            <TagList items={record.metrics} />
          </span>
        </p>
        <p className="text-sm text-gray-700">
          <span className="font-medium text-gray-600">Dimensions:</span>{' '}
          <span className="text-gray-500">
            <TagList items={record.dimensions} />
          </span>
        </p>
        {record.status === 'SUCCEEDED' && record.rowCount !== undefined && (
          <p className="text-xs text-gray-400">{formatNumber(record.rowCount)} rows</p>
        )}
      </div>

      {/* Right column: status + download */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <StatusPill record={record} />
        {record.status === 'SUCCEEDED' && record.downloadUrl && (
          <a
            href={record.downloadUrl}
            download
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Download
          </a>
        )}
      </div>
    </div>
  );
};

// ---- Main Component ----

interface RecentExportsProps {
  /** Increment this value to trigger an immediate refetch (e.g. after enqueuing a new export). */
  refetchTrigger: number;
}

const POLL_INTERVAL_MS = 10_000;

const RecentExports: React.FC<RecentExportsProps> = ({ refetchTrigger }) => {
  const [exports, setExports] = useState<QueryBuilderExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchExports = useCallback(async () => {
    try {
      const data = await listQueryBuilderExports();
      setExports(data.exports);
      setError(null);
      return data.exports;
    } catch {
      setError('Could not load recent exports.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (currentExports: QueryBuilderExportRecord[]) => {
      stopPolling();
      if (!hasActiveJobs(currentExports)) return;

      intervalRef.current = setInterval(async () => {
        if (document.visibilityState === 'hidden') return;
        const updated = await fetchExports();
        if (updated !== null && !hasActiveJobs(updated)) {
          stopPolling();
        }
      }, POLL_INTERVAL_MS);
    },
    [fetchExports, stopPolling]
  );

  // Initial fetch + refetch when trigger changes
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (exports.length === 0) setLoading(true);
      const result = await fetchExports();
      if (cancelled) return;
      if (result !== null) {
        startPolling(result);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchTrigger]);

  // Pause/resume polling on tab visibility change
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Refetch immediately on tab focus, then restart polling if needed
        const updated = await fetchExports();
        if (updated !== null) {
          startPolling(updated);
        }
      }
      // When hidden, the interval tick already checks visibilityState and skips
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchExports, startPolling]);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Recent Exports</h3>

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        {loading && (
          <div className="p-5 text-sm text-gray-400 text-center">Loading recent exports…</div>
        )}

        {!loading && error && (
          <div className="p-5 text-sm text-red-600 text-center">{error}</div>
        )}

        {!loading && !error && exports.length === 0 && (
          <div className="p-5 text-sm text-gray-400 text-center">No recent exports.</div>
        )}

        {!loading && !error && exports.length > 0 && (
          <div>
            {exports.map((record) => (
              <ExportRow key={record.jobId} record={record} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentExports;
